import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';
import { useCareerFocusFeatures } from '../hooks/useStatsigFeatures';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { supabase } from '../utils/supabase';
import { getInternshipStatus, InternshipStatus } from '../utils/internshipStatus';

// Import the V2 dashboard and modals
import InternshipDashboardV2 from '../components/InternshipDashboard/InternshipDashboardV2';
import EnhancedQuickAddModal from '../components/QuickNote/EnhancedQuickAddModal';
import ToDoModal from '../components/ToDoModal';
import { PreparationMode } from '../components/InternshipDashboard/PreparationMode';

const InternshipDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { focusId } = useParams<{ focusId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const { focus, error, loading: focusLoading, refetch } = useActiveCareerFocus('internship', focusId);
  const { features, logEvent } = useCareerFocusFeatures();
  const { showToast } = useToast();
  
  // Modal states
  const [showEnhancedModal, setShowEnhancedModal] = useState(false);
  const [quickAddContext, setQuickAddContext] = useState<{
    title?: string;
    placeholder?: string;
    defaultImpact?: number;
    defaultEngagement?: number;
  } | undefined>();
  const [isToDoModalOpen, setIsToDoModalOpen] = useState(false);
  const [activeToDos, setActiveToDos] = useState<Array<{card: any, todos: any[]}>>([]);
  const [activeFollowUps, setActiveFollowUps] = useState<Array<{card: any, followUps: any[]}>>([]);
  const [isLoadingToDos, setIsLoadingToDos] = useState(false);

  // Load ToDos and Follow-ups when modal opens
  useEffect(() => {
    const loadToDosAndFollowUps = async () => {
      if (!isToDoModalOpen || !user?.id) return;
      
      setIsLoadingToDos(true);
      try {
        // Fetch active to-dos from Project cards
        const { data: todoCards, error: todoError } = await supabase
          .from('career_cards')
          .select('cardid, card_data')
          .eq('user_id', user.id)
          .eq('card_data->>type', 'Project')
          .not('card_data->active_to_do', 'eq', '[]')
          .not('card_data->active_to_do', 'is', null);

        if (todoError) throw todoError;
        
        // Fetch active follow-ups from People cards
        const { data: followUpCards, error: followUpError } = await supabase
          .from('career_cards')
          .select('cardid, card_data')
          .eq('user_id', user.id)
          .eq('card_data->>type', 'People')
          .not('card_data->active_follow_ups', 'eq', '[]')
          .not('card_data->active_follow_ups', 'is', null);

        if (followUpError) throw followUpError;

        // Process to-dos
        const todosWithCards = (todoCards || [])
          .map(card => {
            const todos = card.card_data.active_to_do || [];
            return todos.length > 0 ? { card, todos } : null;
          })
          .filter(Boolean);

        // Process follow-ups
        const followUpsWithCards = (followUpCards || [])
          .map(card => {
            const followUps = card.card_data.active_follow_ups || [];
            return followUps.length > 0 ? { card, followUps } : null;
          })
          .filter(Boolean);

        setActiveToDos(todosWithCards);
        setActiveFollowUps(followUpsWithCards);
      } catch (error) {
        console.error('Error loading todos/follow-ups:', error);
        showToast('Failed to load to-dos', 'error');
      } finally {
        setIsLoadingToDos(false);
      }
    };

    loadToDosAndFollowUps();
  }, [isToDoModalOpen, user?.id, showToast]);

  // Track page view
  useEffect(() => {
    if (focus) {
      logEvent('internship_dashboard_viewed', {
        has_active_focus: true,
        week: focus.current_week,
        total_weeks: focus.total_weeks,
        progress_percentage: focus.progress_percentage
      });
    }
  }, [focus, logEvent]);

  // Handle redirect if no focus
  useEffect(() => {
    if (!authLoading && !focusLoading && user && !focus && !error) {
      const timer = setTimeout(() => {
        navigate('/settings');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [authLoading, focusLoading, user, focus, error, navigate]);

  // Combined loading state
  const loading = authLoading || focusLoading;

  // Show loading state
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading internship dashboard...</p>
        </div>
      </div>
    );
  }

  // If no focus after loading, we'll be redirected
  if (!focus) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Check internship status
  const status = getInternshipStatus(focus);
  
  // Render based on status
  if (status.status === InternshipStatus.UPCOMING) {
    return (
      <PreparationMode
        daysUntilStart={status.metadata.daysUntilStart || 0}
        companyName={focus.company_name || 'Your Internship'}
      />
    );
  }
  
  // For both active and completed internships, show the dashboard
  // The dashboard will handle showing completion status if needed
  return (
    <>
      <InternshipDashboardV2 
        onOpenToDoModal={() => setIsToDoModalOpen(true)} 
        onOpenQuickNote={() => setShowEnhancedModal(true)}
        isCompleted={status.status === InternshipStatus.COMPLETED}
        completionMetadata={status.status === InternshipStatus.COMPLETED ? status.metadata : undefined}
      />
      
      {/* ToDo Modal */}
      <ToDoModal
        isOpen={isToDoModalOpen}
        onClose={() => setIsToDoModalOpen(false)}
        activeToDos={activeToDos}
        activeFollowUps={activeFollowUps}
        isLoading={isLoadingToDos}
        onCardClick={(cardId) => {
          navigate(`/career-card/${cardId}`);
        }}
      />
      
      {/* Enhanced Quick Add Modal */}
      <EnhancedQuickAddModal
        isOpen={showEnhancedModal}
        onClose={() => {
          setShowEnhancedModal(false);
          setQuickAddContext(undefined);
        }}
        context={quickAddContext}
      />
    </>
  );
};

export default InternshipDashboard;